import React, { useEffect, useMemo, useState } from 'react';
import { Col, Row, Tooltip } from 'antd-v5';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PageHeaderLayout from '@/component/page-header-layout';
import dayjs from 'dayjs';
import {
  Select,
  TreeSelect,
  Input,
  Card,
  Button,
  Table,
  DatePicker,
  Form,
} from '@/components/index';
import {
  setQueryFilters,
  setList,
  setPagination,
  setLoading,
  resetStallHealth,
} from '@/state/action/stallHealth';
import {
  exportCxmHealthStatisticRequest,
  getCxmHealthStatisticListRequest,
} from '@/state/request/cxmHealthStatistic';
import downImg from '@/common/images/down.png';
import uploadImg from '@/common/images/upload.png';
import uploadDisableImg from '@/common/images/uploadDisable.png';
import './style.less';
import { getAllDistrictInfo } from '@/state/action/mergeSet';
import { marketList } from '@/state/action/stall';
import { BUSINESS_TYPE, STALL_TYPE, STALL_TYPES } from '@/utils/deviceType';
import { getStartTime, getTodayEndTime } from '@/utils/timer';
import { exportFile } from '@/utils/utils';
import { DEFAULT_FILTERS } from '@/state/reducer/stallHealth';

const { RangePicker } = DatePicker;

const HEALTH_LEVEL = [
  {
    value: 1,
    label: '健康',
    tagStyle: {
      color: '#01B755',
      borderColor: '#6DDE96',
      backgroundColor: '#EEFFF3',
      bgImg: 'linear-gradient(180deg, #F3FFF6 0%, #EAFFF0 100%)',
    },
  },
  {
    value: 2,
    label: '亚健康',
    tagStyle: {
      color: '#1BA200',
      borderColor: '#98DE83',
      backgroundColor: '#F5FFEF',
      bgImg: 'linear-gradient(180deg, #FBFFF8 0%, #F5FFEF 100%)',
    },
  },
  {
    value: 3,
    label: '中风险',
    tagStyle: {
      color: '#FF8003',
      borderColor: '#FFBF81',
      backgroundColor: '#FFFAF6',
      bgImg: 'linear-gradient(180deg, #FFFAF6 0%, #FFF9F3 100%)',
    },
  },
  {
    value: 4,
    label: '高风险',
    tagStyle: {
      color: '#F21717',
      borderColor: '#FF756B',
      backgroundColor: '#FFF0ED',
      bgImg: 'linear-gradient(180deg, #FFF9F7 0%, #FFF0ED 100%)',
    },
  },
];

const MERCHANT_CATEGORY_OPTIONS = [
  { label: '签约商行', value: 1 },
  { label: '试用商行', value: 4 },
];

export function getHealthLevelByLevel(healthLevel) {
  if (healthLevel === null) {
    return {
      value: '',
      label: '--',
      tagStyle: { color: '#181818', borderColor: '#181818', backgroundColor: '#ffffff' },
    };
  }
  let levelItem = HEALTH_LEVEL.find((item) => healthLevel == item.value);
  return levelItem;
}

const HealthTag = ({ level, size = 'medium' }) => {
  const { tagStyle = {}, label, value } = level || {};
  const { color, borderColor, backgroundColor } = tagStyle;
  if (!value) return null;
  return (
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 4,
        border: `1px solid ${borderColor}`,
        color,
        background: backgroundColor,
        width: size === 'small' ? 50 : 50,
        height: size === 'small' ? 24 : 28,
        lineHeight: size === 'small' ? 24 : 28,
        fontSize: size === 'small' ? 14 : 14,
      }}
    >
      {label || '--'}
    </span>
  );
};

const StallHealth = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const {
    queryFilters,
    list,
    healthLevel,
    healthScore,
    total,
    pageNo,
    pageSize,
    loading,
    districtInfo,
    marketData,
  } = useSelector((state) => {
    return {
      ...state.stallHealth,
      districtInfo: state.mergeSet.districtInfo,
      marketData: state.stall.marketData || [],
    };
  });

  const [form] = Form.useForm();
  const [isExpanded, setIsExpanded] = useState(false);

  /** 接口无分页，返回全量数据；前端按 pageNo/pageSize 切片展示 */
  const pageList = useMemo(() => {
    const start = (pageNo - 1) * pageSize;
    return list.slice(start, start + pageSize);
  }, [list, pageNo, pageSize]);

  const buildListParams = (filters) => {
    const params = { ...filters };
    if (filters.pactTime) {
      params.pactStartTimeStart = getStartTime(dayjs(filters.pactTime[0]).valueOf());
      params.pactStartTimeEnd = getTodayEndTime(dayjs(filters.pactTime[1]).valueOf());
    }
    if (filters.serviceEndTime) {
      params.serviceEndTimeStartTime = getStartTime(dayjs(filters.serviceEndTime[0]).valueOf());
      params.serviceEndTimeEndTime = getTodayEndTime(dayjs(filters.serviceEndTime[1]).valueOf());
    }
    delete params.pactTime;
    delete params.serviceEndTime;
    return params;
  };
  /** 请求全量列表并写入 Redux，前端保留分页信息 */
  const fetchList = async (filters) => {
    dispatch(setLoading(true));
    try {
      const params = buildListParams(filters);
      const res = await getCxmHealthStatisticListRequest(params);
      if (res?.code === 200) {
        const rawList = res.data.healthStatisticList ?? [];
        dispatch(
          setList({
            list: rawList,
            total: rawList.length,
            healthLevel: res.data.healthLevel,
            healthScore: res.data.healthScore,
          }),
        );
      } else {
        dispatch(setList({ list: [], total: 0, healthLevel: null, healthScore: null }));
      }
    } catch (err) {
      dispatch(setList({ list: [], total: 0, healthLevel: null, healthScore: null }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    const formValues = { ...queryFilters };
    form.setFieldsValue(formValues);
    dispatch(getAllDistrictInfo());
    dispatch(marketList());
    fetchList(queryFilters);
  }, []);

  const handleExport = async () => {
    form.validateFields().then(async (values) => {
      form.submit();
      const params = buildListParams(values);
      const response = await exportCxmHealthStatisticRequest(params);
      exportFile(response, '商行健康度.xlsx');
    });
  };

  const renderSummary = () => {
    const level = getHealthLevelByLevel(healthLevel);
    const { tagStyle = {} } = level || {};
    const { color } = tagStyle;
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <HealthTag level={level} />
          <span style={{ fontSize: 16, fontWeight: 600, color }}>
            {healthScore === null ? '--' : `${healthScore}分`}
          </span>
        </div>
        <Button
          type='primary'
          onClick={handleExport}
          disabled={pageList.length === 0}
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <img
            src={pageList.length === 0 ? uploadDisableImg : uploadImg}
            alt='upload'
            style={{ width: 16, height: 16, marginRight: 4 }}
          />
          导出
        </Button>
      </div>
    );
  };

  const cityOptions = useMemo(() => {
    const { mapping } = districtInfo || {};
    const { origins } = mapping || [];
    //去掉区
    if (origins instanceof Array) {
      for (const iterator of origins) {
        for (const iteratorChildren of iterator.children) {
          delete iteratorChildren.children;
        }
      }
    }
    return origins;
  }, [districtInfo]);
  const pagination = useMemo(
    () => ({
      current: pageNo,
      pageSize,
      total,
    }),
    [pageNo, pageSize, total],
  );

  const columns = useMemo(
    () => [
      {
        title: '商行业务ID',
        dataIndex: 'businessCode',
        key: 'businessCode',
        width: 106,
        render: (text, { stallId }) => {
          return (
            <span
              style={{ color: '#01B755', cursor: 'pointer' }}
              onClick={() => {
                history.push({
                  pathname: '/booth/stallManager/stallManagementDetail',
                  state: { stallId, from: 'GO_BACK' },
                });
              }}
            >
              {text}
            </span>
          );
        },
      },
      {
        title: '商行名称',
        dataIndex: 'stallName',
        key: 'stallName',
        render: (text) => {
          return (
            <Tooltip title={text} placement='topLeft'>
              <span className='text-ellipsis-2'>{text}</span>
            </Tooltip>
          );
        },
        width: 180,
      },
      {
        title: '经营品类',
        dataIndex: 'businessType',
        key: 'businessType',
        render: (text) => BUSINESS_TYPE[text],
        width: 96,
      },
      {
        title: '健康等级',
        dataIndex: 'healthLevel',
        key: 'healthLevel',
        render: (text) => (
          <div style={{ marginLeft: 4 }}>
            <HealthTag level={getHealthLevelByLevel(text)} size='small' />
          </div>
        ),
        width: 96,
      },
      { title: '健康度(分)', dataIndex: 'healthScore', key: 'healthScore', width: 110 },
      {
        title: '城市',
        dataIndex: 'district',
        key: 'district',
        render: (_, record) => {
          return `${record.provinceName || ''} ${record.cityName || ''} `;
        },
        width: 120,
      },
      {
        title: '市场名称',
        dataIndex: 'marketName',
        key: 'marketName',
        render: (text) => {
          return (
            <Tooltip title={text} placement='topLeft'>
              <span className='text-ellipsis-2'>{text}</span>
            </Tooltip>
          );
        },
        width: 180,
      },
      {
        title: '商行版本',
        dataIndex: 'type',
        key: 'type',
        render: (text) => {
          return text ? STALL_TYPE.get(text) : '';
        },
        width: 96,
      },
      {
        title: '签约日期',
        dataIndex: 'pactStartTime',
        key: 'pactStartTime',
        width: 120,
        render: (text) => {
          return (text && dayjs(text).format('YYYY-MM-DD')) || '--';
        },
      },
      {
        title: '到期日期',
        dataIndex: 'serviceEndTime',
        key: 'serviceEndTime',
        width: 120,
        render: (text) => {
          return (text && dayjs(text).format('YYYY-MM-DD')) || '--';
        },
      },
      {
        title: '商行分类',
        dataIndex: 'pactStatus',
        key: 'pactStatus',
        render: (text) => {
          return STALL_TYPES.get(text) || '';
        },
        width: 96,
      },
      {
        title: '操作',
        key: 'action',
        fixed: 'right',
        align: 'center',
        width: 72,
        render: (_, record) => (
          <a
            style={{ color: '#01B755' }}
            onClick={(e) => {
              e.preventDefault();
              history.push(`/statics/stallHealth/detail/${record.stallId}`);
            }}
          >
            详情
          </a>
        ),
      },
    ],
    [],
  );

  const handleFinish = (values) => {
    const nextFilters = values;
    dispatch(setQueryFilters(nextFilters));
    dispatch(setPagination({ pageNo: 1 }));
    fetchList(nextFilters);
  };

  const handleReset = () => {
    form.resetFields();
    dispatch(resetStallHealth());
    fetchList(DEFAULT_FILTERS);
  };

  const handleToggleExpand = () => setIsExpanded((v) => !v);

  /** 翻页/改每页条数仅更新前端分页状态，不请求接口 */
  const handleTableChange = (p) => {
    const nextPageNo = p && p.current ? p.current : 1;
    const nextPageSize = p && p.pageSize ? p.pageSize : pageSize;
    dispatch(setPagination({ pageNo: nextPageNo, pageSize: nextPageSize }));
  };

  const formItemLayout = {
    labelCol: { flex: '82px' },
    wrapperCol: { flex: 'auto', width: 0 },
  };

  const searchDisplay = useMemo(() => {
    return isExpanded ? 'block' : 'none';
  }, [isExpanded]);

  const topColResponsiveProps = {
    xs: 24,
    sm: 24,
    md: 12,
    lg: 12,
    xl: 8,
    xxl: 6,
  };
  const topColResponsivePropsExpand = useMemo(() => {
    return {
      xs: isExpanded ? 24 : 0,
      sm: isExpanded ? 24 : 0,
      md: isExpanded ? 12 : 0,
      lg: isExpanded ? 12 : 0,
      xl: isExpanded ? 8 : 0,
      xxl: 6,
    };
  }, [isExpanded]);
  return (
    <PageHeaderLayout>
      <div style={{ paddingBottom: 12 }} className='stall-health-container'>
        <Card bordered={false} bodyStyle={{ padding: '0 0 16px 16px' }}>
          <Form
            form={form}
            layout='inline'
            onFinish={handleFinish}
            {...formItemLayout}
            initialValues={DEFAULT_FILTERS}
          >
            <Row style={{ width: '100%' }}>
              <Col {...topColResponsiveProps} style={{ marginTop: 16 }}>
                <Form.Item label='城市' name='districtList'>
                  <TreeSelect
                    allowClear
                    showSearch
                    dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                    treeData={cityOptions}
                    placeholder='选择城市'
                    treeDefaultExpandAll={false}
                    filterTreeNode={(input, treeNode) => {
                      return treeNode.props.title.indexOf(input) >= 0;
                    }}
                    multiple
                    maxTagCount={2}
                  />
                </Form.Item>
              </Col>
              <Col {...topColResponsiveProps} style={{ marginTop: 16 }}>
                <Form.Item label='市场名称' name='marketIdList'>
                  <Select
                    style={{ width: '100%' }}
                    showSearch
                    multiple
                    maxTagCount={2}
                    placeholder='选择市场'
                    options={marketData.map((item) => ({
                      label: item.name,
                      value: item.id,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col {...topColResponsiveProps} style={{ marginTop: 16 }}>
                <Form.Item label='商行业务ID' name='businessCode'>
                  <Input style={{ width: '100%' }} allowClear placeholder='输入商行业务ID' />
                </Form.Item>
              </Col>
              <Col {...topColResponsiveProps} style={{ marginTop: 16 }}>
                <Form.Item label='商行名称' name='name'>
                  <Input style={{ width: '100%' }} allowClear placeholder='输入商行名称' />
                </Form.Item>
              </Col>
              <Col {...topColResponsiveProps} style={{ marginTop: 16 }}>
                <Form.Item label='健康等级' name='healthLevel'>
                  <Select
                    style={{ width: '100%' }}
                    showSearch
                    placeholder='选择健康等级'
                    options={HEALTH_LEVEL}
                    multiple
                    maxTagCount={2}
                  />
                </Form.Item>
              </Col>
              <Col {...topColResponsivePropsExpand} style={{ marginTop: 16 }}>
                <Form.Item label='经营品类' name='businessTypeList'>
                  <Select
                    style={{ width: '100%' }}
                    showSearch
                    placeholder='选择经营品类'
                    options={Object.keys(BUSINESS_TYPE).map((item) => ({
                      label: BUSINESS_TYPE[item],
                      value: item,
                    }))}
                    multiple
                    maxTagCount={2}
                  />
                </Form.Item>
              </Col>
              <Col {...topColResponsivePropsExpand} style={{ marginTop: 16 }}>
                <Form.Item label='商行版本' name='typeList'>
                  <Select
                    style={{ width: '100%' }}
                    showSearch
                    placeholder='选择商行版本'
                    options={[...STALL_TYPE].slice(1).map(([k, v]) => ({ label: v, value: k }))}
                    multiple
                    maxTagCount={2}
                  />
                </Form.Item>
              </Col>
              <Col {...topColResponsiveProps} style={{ marginTop: 16, display: searchDisplay }}>
                <Form.Item label='签约日期' name='pactTime'>
                  <RangePicker
                    className='ui-date-picker'
                    style={{ width: '100%' }}
                    format='YYYY-MM-DD'
                  />
                </Form.Item>
              </Col>
              <Col {...topColResponsiveProps} style={{ marginTop: 16, display: searchDisplay }}>
                <Form.Item label='到期日期' name='serviceEndTime'>
                  <RangePicker
                    className='ui-date-picker'
                    style={{ width: '100%' }}
                    format='YYYY-MM-DD'
                  />
                </Form.Item>
              </Col>
              <Col {...topColResponsiveProps} style={{ marginTop: 16, display: searchDisplay }}>
                <Form.Item label='商行分类' name='pactStatusList'>
                  <Select
                    style={{ width: '100%' }}
                    showSearch
                    placeholder='选择商行分类'
                    options={MERCHANT_CATEGORY_OPTIONS}
                    multiple
                    maxTagCount={2}
                  />
                </Form.Item>
              </Col>
              <Col
                {...topColResponsiveProps}
                style={{ marginTop: 16, display: searchDisplay }}
              ></Col>
              <Col {...topColResponsiveProps} style={{ marginTop: 16, paddingRight: 16 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 8,
                  }}
                >
                  <Button type='primary' htmlType='submit'>
                    查询
                  </Button>
                  <Button onClick={handleReset}>重置</Button>
                  <a
                    href='/'
                    onClick={(e) => {
                      e.preventDefault();
                      handleToggleExpand();
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: '#5E5E5E',
                      padding: '0px 6px',
                    }}
                  >
                    {isExpanded ? '收起' : '展开'}{' '}
                    {
                      <img
                        src={downImg}
                        alt='down'
                        style={{
                          width: 16,
                          height: 16,
                          marginLeft: 4,
                          transform: isExpanded ? 'rotate(180deg)' : 'none',
                        }}
                      />
                    }
                  </a>
                </div>
              </Col>
            </Row>
          </Form>
        </Card>

        <Card bordered={false} style={{ marginTop: 8 }}>
          {renderSummary()}
          <Table
            dataSource={pageList}
            loading={loading}
            pagination={pagination}
            onChange={handleTableChange}
            scroll={{ x: 1400 }}
            columns={columns}
          />
        </Card>
      </div>
    </PageHeaderLayout>
  );
};

export default StallHealth;
